import { makeStyles } from "tss-react/mui";
import { MiIcon, MiIconButton } from "@miplatform/components";
import { ICalendarWorkTemplateBase } from "@miplatform/types";
import { mdiChevronDown, mdiChevronUp, mdiMinus, mdiPlus } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { ICalendarWorkCapacityConfig } from "../../ConfigCalendarSetList.types";

export const CalendarWorkCapacityCard = ({
  config,
  orderTemplate: { id, name },
  isReadOnly,
  isFirstInList,
  isLastInList,
  isIncrementDisabled,
  onQuantityChange,
  onReorderUp,
  onReorderDown,
}: Readonly<CalendarWorkCapacityCardProps>) => {
  const { classes, theme } = useStyles();

  const handlePlusClick = () => {
    onQuantityChange(id, (config?.amount || 0) + 1);
  };

  const handleMinusClick = () => {
    onQuantityChange(id, Math.max((config?.amount || 0) - 1, 0));
  };

  return (
    <Box className={classes.configListCardContent}>
      <Box className={classes.configListCard}>
        <Box className={classes.configListCardContent}>
          <Box className={classes.configListCardText}>
            <Box className={classes.configListCardTextLine}>
              <Typography variant="HD2" color={theme.palette.primary.main}>
                {name}
              </Typography>
            </Box>
          </Box>
        </Box>
        {!isReadOnly && (
          <Box className={classes.configQuantityContent}>
            <MiIconButton
              onClick={handleMinusClick}
              disabled={(config?.amount || 0) === 0}
            >
              <MiIcon path={mdiMinus} color={theme.palette.primary.main} />
            </MiIconButton>
            <Typography>{config?.amount || 0}</Typography>
            <MiIconButton
              onClick={handlePlusClick}
              disabled={isIncrementDisabled}
            >
              <MiIcon path={mdiPlus} color={theme.palette.primary.main} />
            </MiIconButton>
          </Box>
        )}
      </Box>
      {!isReadOnly && (
        <Box className={classes.arrowsContainer}>
          <MiIcon
            path={mdiChevronUp}
            color={theme.palette.primary.main}
            onClick={() => onReorderUp(id)}
            disabled={isFirstInList}
            size={0.85}
          />
          <MiIcon
            path={mdiChevronDown}
            color={theme.palette.primary.main}
            onClick={() => onReorderDown(id)}
            disabled={isLastInList}
            size={0.85}
          />
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  configListCard: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    flex: 1,
  },
  configListCardContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  configQuantityContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    border: `1px solid ${theme.palette.grey.darkAccent}`,
    borderRadius: theme.spacing(1),
    height: 34,
  },
  configListCardText: {
    display: "flex",
    flexDirection: "column",
  },
  configListCardTextLine: {
    height: 16,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  arrowsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
}));

export interface CalendarWorkCapacityCardProps {
  config?: ICalendarWorkCapacityConfig;
  orderTemplate: ICalendarWorkTemplateBase;
  isReadOnly?: boolean;
  isFirstInList?: boolean;
  isLastInList?: boolean;
  isIncrementDisabled?: boolean;
  onQuantityChange: (orderTemplateId: string, quantity: number) => void;
  onReorderUp: (orderTemplateId: string) => void;
  onReorderDown: (orderTemplateId: string) => void;
}
